import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { get as lodashGet, isBoolean, isObject } from "lodash";

import { phoneExpireTime, baseRoute, SMS_CODE } from "src/config";
import { useActions } from "src/hooks/useActions";
import { sendGAFormEvent } from "src/methods/ga";
import { getStores } from "src/actions/stores";
import {
  postQuestionnaire,
  postSmsCode,
  postSmsVerify,
} from "src/actions/questionnaire";
import { useFormValues } from "src/hooks/useFormValues";
import { useAuth } from "src/providers/Auth";
import QuestionnaireForm from "./QuestionnaireForm";
import ActivityEndModal from "src/components/ActivityEndModal";
import ErrorModal from "./ErrorModal";
import PrivacyModal from "./PrivacyModal";
import { useToast } from "src/providers/Toast";
import Loading from "src/components/Loading";
import { finishFormTrack } from "src/methods/fb";
import { getUtm } from "src/utils/method";

const MODAL_TEXT = {
  privacy: "privacy",
  activityEnd: "activityEnd",
};
const Questionnaire = ({ checkVerify }) => {
  const {
    auth: { accessToken, userId },
    profile,
  } = useAuth();
  const { danger, success } = useToast();
  const navigate = useNavigate();
  const [
    actionPostQuestionnaire,
    actionPostSmsCode,
    actionPostSmsVerify,
    actionGetStores,
  ] = useActions([postQuestionnaire, postSmsCode, postSmsVerify, getStores]);
  const { phone: phoneFormValue = "" } = useFormValues({
    fieldsNames: ["phone"],
    formName: "laPrairieForm",
  });

  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCodeVerified, setIsCodeVerified] = useState(false);
  const [getCode, setGetCode] = useState(false);
  const [verifyError, setVerifyError] = useState(false);
  const [codeSubmitting, setCodeSubmitting] = useState(false);

  const authVerifyResp = useSelector((state) => state.ajax.authVerify);
  const hasCanUseCouponResp = useSelector(
    (state) => state.ajax.hasCanUseCoupon
  );
  const hasCanUseCoupon = lodashGet(
    hasCanUseCouponResp,
    ["data", "result"],
    true
  );

  const getVerifyCode = async () => {
    setCodeSubmitting(true);
    setVerifyError(false);

    const res =
      (await actionPostSmsCode({ phoneNumber: phoneFormValue })) || {};
    if (res.status === 200) {
      setGetCode(true);
      sendGAFormEvent(userId, "/sendSms");
      setTimeout(() => {
        if (!isCodeVerified) setGetCode(false);
      }, phoneExpireTime);
    } else {
      danger("簡訊發送錯誤");
    }
    setCodeSubmitting(false);
  };

  const submitVerifyCode = async ({ verifyCode, phoneNumber }) => {
    setVerifyError(false);
    const { data } = await actionPostSmsVerify({
      phoneNumber,
      verifyCode,
    });
    if (data && data.token) {
      sendGAFormEvent(userId, "verifyPhone");
      setIsCodeVerified(true);
      return true;
    } else {
      setVerifyError(true);
      danger("驗證碼錯誤");
      return false;
    }
  };

  const submitForm = async ({
    lastName = "",
    firstName = "",
    birthdayYear,
    birthdayMonth,
    birthdayDay,
    email = "",
    phone,
    store,
    verifySms,
    gender,
  }) => {
    let verifyCodeSuccess = true;

    if (!isCodeVerified && verifySms !== SMS_CODE && profile.phone !== phone) {
      verifyCodeSuccess = await submitVerifyCode({
        verifyCode: verifySms,
        phoneNumber: phone,
      });
    }

    if (verifyCodeSuccess) {
      const utm = getUtm();
      const body = {
        accessToken: accessToken,
        lastName: lastName.trim(),
        firstName: firstName.trim(),
        email: email.trim(),
        birthday: `${birthdayYear}/${birthdayMonth}/${birthdayDay}`,
        phone,
        fillinBranch: store,
        nameTitle: gender,
        utm,
      };
      const res = (await actionPostQuestionnaire(body)) || {};
      if (!res || res.status !== 200) {
        danger("送出失敗");
      } else {
        success("送出成功");
        sendGAFormEvent(userId, "submit");
        finishFormTrack();
        if (isBoolean(hasCanUseCoupon) && hasCanUseCoupon) {
          navigate(`${baseRoute}/couponList`, { replace: true });
        } else {
          setShowModal(MODAL_TEXT.activityEnd);
        }
      }
    }
  };

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      actionGetStores({ userId });
      setIsLoading(false);
    }
    if (userId) {
      fetchData();
      setIsCodeVerified(!!profile.phone);
    }
  }, [userId]);

  useEffect(() => {
    if (authVerifyResp && !hasCanUseCoupon)
      setShowModal(MODAL_TEXT.activityEnd);
  }, [authVerifyResp]);

  return (
    <>
      {isLoading && <Loading />}
      <QuestionnaireForm
        verifyError={verifyError}
        onSubmit={submitForm}
        getVerifyCode={getVerifyCode}
        isCodeVerified={isCodeVerified}
        getCode={getCode}
        setGetCode={setGetCode}
        codeSubmitting={codeSubmitting}
        setIsCodeVerified={setIsCodeVerified}
        setShowModal={setShowModal}
        profile={profile}
      />
      <PrivacyModal
        open={showModal === MODAL_TEXT.privacy}
        setOpen={setShowModal}
      />
      <ActivityEndModal
        open={showModal === MODAL_TEXT.activityEnd}
        setOpen={() => {}}
      />
      <ErrorModal
        errors={showModal}
        open={showModal && isObject(showModal)}
        setOpen={setShowModal}
      />
    </>
  );
};

export default Questionnaire;
